/**
 * RegressionAlertBanner — Large banner showing regression severity at top of recurring page.
 */

import type { RecurringAlertSummary } from "@accessimonitor/contracts";

interface RegressionAlertBannerProps {
  alert: RecurringAlertSummary;
}

const SEVERITY_STYLES: Record<
  string,
  { bg: string; text: string; icon: string }
> = {
  critical: {
    bg: "bg-danger",
    text: "text-white",
    icon: "🚨",
  },
  warning: {
    bg: "bg-warning",
    text: "text-dark",
    icon: "⚠️",
  },
  info: {
    bg: "bg-info",
    text: "text-white",
    icon: "ℹ️",
  },
};

export function RegressionAlertBanner({
  alert,
}: RegressionAlertBannerProps) {
  if (!alert.regressionDetected && alert.severity === "info") {
    return null; // No banner for info severity
  }

  const styles = SEVERITY_STYLES[alert.severity];

  return (
    <div
      className={`alert alert--${alert.severity} ${styles.bg} ${styles.text} mb-3`}
      role="alert"
    >
      <div className="alert-content">
        <div className="alert-header">
          <span className="alert-icon">{styles.icon}</span>
          <h3 className="alert-title mb-0">
            {alert.notificationPayload.title}
          </h3>
        </div>
        <p className="alert-summary mb-2">
          {alert.notificationPayload.summary}
        </p>
        {alert.reasons.length > 0 && (
          <ul className="alert-reasons mb-0">
            {alert.reasons.map((reason, idx) => (
              <li key={idx}>{reason}</li>
            ))}
          </ul>
        )}
        {alert.recommendedAction && (
          <p className="alert-action mt-2 mb-0">
            <strong>Recommended:</strong> {alert.recommendedAction}
          </p>
        )}
      </div>
    </div>
  );
}
